import React from "react";
import {
  ChevronLeft,
  ChevronRight,
  Globe,
  Link2,
  Pencil,
  Trash2,
  UserCheck,
} from "lucide-react";
import type { SspInterconnectionResponse } from "../../../../api/frameworksApi";
import {
  FormField,
  FormInput,
  FormSelect,
  FormTextarea,
} from "../../../components/Modal";
import { Tip } from "../../../components/ui/tooltip";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { getPageWindow } from "../utils/pagination";

import type { InterconnectionFormShape } from "../types";
export type { InterconnectionFormShape };

export interface InterconnectionsTabProps {
  interconnectionsLoading: boolean;
  interconnections: SspInterconnectionResponse[];
  pagInterconnections: SspInterconnectionResponse[];
  filtInterconnections: SspInterconnectionResponse[];
  editingInterconnectionId: string | null;
  setEditingInterconnectionId: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  editInterconnectionForm: InterconnectionFormShape;
  setEditInterconnectionForm: React.Dispatch<
    React.SetStateAction<InterconnectionFormShape>
  >;
  savingInterconnection: boolean;
  handleUpdateInterconnection: () => void;
  isPublishedSsp: boolean;
  handleDeleteInterconnection: (connId: string) => Promise<void> | void;
  deletingInterconnectionId: string | null;
  tabPage: number;
  tabPageSize: number;
  setCurrentTabPagination: (pagination: {
    page: number;
    pageSize: number;
  }) => void;
}

export function InterconnectionsTab({
  interconnectionsLoading,
  interconnections,
  pagInterconnections,
  filtInterconnections,
  editingInterconnectionId,
  setEditingInterconnectionId,
  editInterconnectionForm,
  setEditInterconnectionForm,
  savingInterconnection,
  handleUpdateInterconnection,
  isPublishedSsp,
  handleDeleteInterconnection,
  deletingInterconnectionId,
  tabPage,
  tabPageSize,
  setCurrentTabPagination,
}: InterconnectionsTabProps) {
  return (
    <>
      {interconnectionsLoading ? (
        <LoadingSpinner text="Loading interconnections" />
      ) : interconnections.length > 0 ? (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {pagInterconnections.map((conn) => (
              <div
                key={conn.id}
                className="rounded-xl border border-border bg-card p-4 hover:shadow-md hover:border-indigo-200 dark:hover:border-indigo-500/30 transition-all"
              >
                {editingInterconnectionId === conn.id ? (
                  /* ── Inline edit form ── */
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="Title" required>
                        <FormInput
                          value={editInterconnectionForm.title}
                          onChange={(v) =>
                            setEditInterconnectionForm((f) => ({
                              ...f,
                              title: v,
                            }))
                          }
                          placeholder="Interconnection title"
                        />
                      </FormField>
                      <FormField label="Direction">
                        <FormSelect
                          options={[
                            { label: "Incoming", value: "incoming" },
                            { label: "Outgoing", value: "outgoing" },
                            {
                              label: "Incoming-Outgoing",
                              value: "incoming-outgoing",
                            },
                          ]}
                          value={editInterconnectionForm.direction}
                          onChange={(v) =>
                            setEditInterconnectionForm((f) => ({
                              ...f,
                              direction: v as typeof f.direction,
                            }))
                          }
                          placeholder="Select direction"
                        />
                      </FormField>
                    </div>
                    <FormField label="Description">
                      <FormTextarea
                        value={editInterconnectionForm.description}
                        onChange={(v) =>
                          setEditInterconnectionForm((f) => ({
                            ...f,
                            description: v,
                          }))
                        }
                        placeholder="Description"
                      />
                    </FormField>
                    <div className="grid grid-cols-2 gap-3">
                      <FormField label="Remote System Name">
                        <FormInput
                          value={editInterconnectionForm.remoteSystemName}
                          onChange={(v) =>
                            setEditInterconnectionForm((f) => ({
                              ...f,
                              remoteSystemName: v,
                            }))
                          }
                          placeholder="Remote system"
                        />
                      </FormField>
                      <FormField label="Responsible Roles (comma-separated)">
                        <FormInput
                          value={editInterconnectionForm.responsibleRoles}
                          onChange={(v) =>
                            setEditInterconnectionForm((f) => ({
                              ...f,
                              responsibleRoles: v,
                            }))
                          }
                          placeholder="role-1, role-2"
                        />
                      </FormField>
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <button
                        onClick={() => setEditingInterconnectionId(null)}
                        className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdateInterconnection}
                        disabled={
                          !editInterconnectionForm.title ||
                          savingInterconnection
                        }
                        className="px-3 py-1.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-50"
                      >
                        {savingInterconnection ? "Saving" : "Save"}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* ── Read-only card display ── */
                  (() => {
                    const dirKey = (conn.direction || "").toLowerCase();
                    const dirTint = /incoming.*outgoing/.test(dirKey)
                      ? "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-500/10 dark:text-violet-400 dark:border-violet-500/30"
                      : dirKey === "incoming"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/30"
                        : dirKey === "outgoing"
                          ? "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/30"
                          : "bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-500/10 dark:text-slate-300 dark:border-slate-500/30";
                    return (
                      <div className="flex flex-col gap-3 h-full">
                        {/* Header */}
                        <div className="flex items-start gap-3">
                          <div className="flex items-center justify-center size-10 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex-shrink-0 ring-1 ring-indigo-100 dark:ring-indigo-500/20">
                            <Link2 className="size-5" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-sm font-semibold text-foreground break-words">{conn.title}</p>
                              <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border capitalize flex-shrink-0 ${dirTint}`}>
                                {conn.direction?.replace(/-/g, " ")}
                              </span>
                            </div>
                            {conn.description && (
                              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                                {conn.description}
                              </p>
                            )}
                          </div>
                          {!isPublishedSsp && (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Tip label="Edit Interconnection" side="bottom">
                                <button
                                  onClick={() => {
                                    setEditingInterconnectionId(conn.id);
                                    setEditInterconnectionForm({
                                      title: conn.title,
                                      description: conn.description || "",
                                      direction: conn.direction as any,
                                      remoteSystemName: conn.remoteSystemName || "",
                                      responsibleRoles:
                                        conn.responsibleRoles?.map((r) => r.role_id).join(", ") || "",
                                      protocols: conn.protocols?.length
                                        ? conn.protocols
                                        : [{ name: "", portRanges: [{ start: "", end: "", transport: "tcp" }] }],
                                    });
                                  }}
                                  className="p-1.5 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-500/10 text-muted-foreground hover:text-indigo-600 transition-colors"
                                >
                                  <Pencil className="size-3.5 hover:text-indigo-600" />
                                </button>
                              </Tip>
                              <Tip label="Delete Interconnection" side="bottom">
                                <button
                                  onClick={() => handleDeleteInterconnection(conn.id)}
                                  disabled={deletingInterconnectionId === conn.id}
                                  className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 text-muted-foreground hover:text-red-600 transition-colors disabled:opacity-50"
                                >
                                  <Trash2 className="size-3.5 hover:text-red-600" />
                                </button>
                              </Tip>
                            </div>
                          )}
                        </div>

                        {/* Meta */}
                        {(conn.remoteSystemName || (conn.responsibleRoles && conn.responsibleRoles.length > 0)) && (
                          <div className="flex flex-col gap-1.5 pl-[52px]">
                            {conn.remoteSystemName && (
                              <div className="flex items-center gap-2 text-xs">
                                <Globe className="size-3.5 text-cyan-600 dark:text-cyan-400 flex-shrink-0" />
                                <span className="text-muted-foreground">Remote:</span>
                                <span className="text-foreground font-medium break-words min-w-0">{conn.remoteSystemName}</span>
                              </div>
                            )}
                            {conn.responsibleRoles?.length > 0 && (
                              <div className="flex items-start gap-2 text-xs">
                                <UserCheck className="size-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                                <span className="text-muted-foreground flex-shrink-0">Roles:</span>
                                <div className="flex flex-wrap gap-1">
                                  {conn.responsibleRoles.map((r) => (
                                    <span
                                      key={r.role_id}
                                      className="inline-flex items-center text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border"
                                    >
                                      {r.role_id}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Protocols */}
                        {conn.protocols?.length > 0 && (
                          <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-border/60 mt-auto">
                            <span className="text-[10px] text-muted-foreground font-medium">Protocols:</span>
                            {conn.protocols.map((p, i) => {
                              const pn = (p.name || "").toLowerCase();
                              const pTint = /https|tls|ssl/.test(pn)
                                ? "bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20"
                                : /saml|oidc|oauth|ldap/.test(pn)
                                  ? "bg-violet-50 text-violet-700 border-violet-100 dark:bg-violet-500/10 dark:text-violet-400 dark:border-violet-500/20"
                                  : /syslog|log/.test(pn)
                                    ? "bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20"
                                    : /jdbc|sql|db/.test(pn)
                                      ? "bg-blue-50 text-blue-700 border-blue-100 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20"
                                      : /http|rest|api/.test(pn)
                                        ? "bg-cyan-50 text-cyan-700 border-cyan-100 dark:bg-cyan-500/10 dark:text-cyan-400 dark:border-cyan-500/20"
                                        : "bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-500/10 dark:text-slate-300 dark:border-slate-500/20";
                              return (
                                <span
                                  key={i}
                                  className={`inline-flex items-center text-[10px] font-mono font-semibold px-2 py-0.5 rounded-md border ${pTint}`}
                                >
                                  {p.name}
                                  {p.portRanges?.[0]?.start && `:${p.portRanges[0].start}`}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()
                )}
              </div>
            ))}
          </div>
          {filtInterconnections.length > 0 && (
            <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-[#e2e8f0] dark:border-white/[0.06] bg-[#fafbfc] dark:bg-white/[0.015] flex-shrink-0">
              <p className="text-[11px] leading-tight text-[#475569] dark:text-white/70">
                Showing{" "}
                <span className="font-semibold text-[#334155] dark:text-white/90">
                  {Math.min((tabPage - 1) * tabPageSize + 1, filtInterconnections.length)}–{Math.min(tabPage * tabPageSize, filtInterconnections.length)}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-[#334155] dark:text-white/90">
                  {filtInterconnections.length}
                </span>
              </p>
              {Math.ceil(filtInterconnections.length / tabPageSize) > 1 && (
                <div className="flex items-center gap-0.5">
                  <button
                    disabled={tabPage === 1}
                    onClick={() => setCurrentTabPagination({ page: Math.max(1, tabPage - 1), pageSize: tabPageSize })}
                    className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="size-3" />
                  </button>

                  {getPageWindow(tabPage, Math.ceil(filtInterconnections.length / tabPageSize)).map((p, idx) =>
                    p === "dots" ? (
                      <span
                        key={`dots-${idx}`}
                        className="w-6 h-6 flex items-center justify-center text-xs text-[#475569] dark:text-white/70 select-none"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setCurrentTabPagination({ page: p as number, pageSize: tabPageSize })}
                        className={`w-6 h-6 text-xs font-medium rounded-md transition-colors ${p === tabPage
                          ? "bg-indigo-600 dark:bg-indigo-600 text-white"
                          : "text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04]"
                          }`}
                      >
                        {p}
                      </button>
                    ),
                  )}

                  <button
                    disabled={tabPage >= Math.ceil(filtInterconnections.length / tabPageSize)}
                    onClick={() => setCurrentTabPagination({ page: Math.min(Math.ceil(filtInterconnections.length / tabPageSize), tabPage + 1), pageSize: tabPageSize })}
                    className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="size-3" />
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="rounded-lg border border-border bg-card p-5 text-center py-12">
          <Link2 className="size-8 text-muted-foreground mb-3 mx-auto" />
          <p className="text-sm font-medium">No interconnections defined</p>
          <p className="text-xs text-muted-foreground mt-1">
            Add system interconnections and external integrations
          </p>
        </div>
      )}
    </>
  );
}
